import { QueryClient, keepPreviousData, useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { Paginated } from '@denwen-msp/shared';
import { ApiError, api, errorMessage, type ApiOptions } from './api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
    },
    mutations: {
      // Mutations show their own toast unless they handle errors themselves (forms map fields).
      onError: (err) => {
        if (err instanceof ApiError && err.fields && Object.keys(err.fields).length) return;
        toast.error(errorMessage(err));
      },
    },
  },
});

/** GET with caching. `useApi<Org>(['organizations', id], `/organizations/${id}`)` */
export function useApi<T>(key: QueryKey, path: string, opts: { query?: ApiOptions['query']; enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...key, opts.query ?? {}],
    queryFn: ({ signal }) => api<T>(path, { query: opts.query, signal }),
    enabled: opts.enabled ?? true,
  });
}

/** Paginated list; keeps the previous page on screen while the next one loads. */
export function useList<T>(key: QueryKey, path: string, query: ApiOptions['query'] = {}, enabled = true) {
  return useQuery({
    queryKey: [...key, 'list', query],
    queryFn: ({ signal }) => api<Paginated<T>>(path, { query, signal }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/**
 * Write call that refreshes the given query keys afterwards.
 * `const save = useApiMutation((body) => api('/x', { method: 'POST', body }), [['x']], 'Saved')`
 */
export function useApiMutation<TVars, TResult = unknown>(fn: (vars: TVars) => Promise<TResult>, invalidate: QueryKey[] = [], successMessage?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all(invalidate.map((key) => qc.invalidateQueries({ queryKey: key })));
      if (successMessage) toast.success(successMessage);
    },
  });
}
